"use client";

import { useEffect } from "react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useHreflang } from "@/i18n/useHreflang";
import HeaderStrip from "./components/HeaderStrip";
import HeroSection from "./components/HeroSection";
import StatsSection from "./components/StatsSection";
import WhatWeDo from "./components/WhatWeDo";
import WhyItMatters from "./components/WhyItMatters";
import CommonIssues from "./components/CommonIssues";
import ScopeSection from "./components/ScopeSection";
import NeighborhoodCoverage from "./components/NeighborhoodCoverage";
import FaqSection from "@/components/feature/FaqSection";
import ServiceAreasGrid from "@/components/feature/ServiceAreasGrid";
import RelatedServicesBlockService from "@/components/feature/RelatedServicesBlockService";
import BottomCta from "./components/BottomCta";
import LeadFormSection from "@/components/feature/LeadFormSection";
import AboutServiceSection from "@/components/feature/AboutServiceSection";
import { injectMetaTags } from "@/lib/seoHelpers";

export default function WeatherstrippingSealingPage() {
  const { t, currentLang } = useLanguage();
  useHreflang();

  const weatherAboutImg =
    "https://storage.readdy-site.link/project_files/76915537-ee3f-4564-9a23-41bd98b5df27/4f5ebda1-5f45-4c79-ba36-516b571d222f_Stop-Drafts-and-Water-Intrusion-in-Miami-Homes.jpg?v=1240c3a97fe40b2b53ba87a12aa2e5c5";

  const weatherFaqs = [
    {
      question: t("weather.faq1Q") as string,
      answer: t("weather.faq1A") as string,
    },
    {
      question: t("weather.faq2Q") as string,
      answer: t("weather.faq2A") as string,
    },
    {
      question: t("weather.faq3Q") as string,
      answer: t("weather.faq3A") as string,
    },
  ];

  useEffect(() => {
    document.title = currentLang === "es"
      ? "Burletes y Sellado Miami \u2014 Calidad Marina Costera"
      : "Weatherstripping & Sealing Miami \u2014 Marine-Grade Coastal";

    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute(
      "content",
      currentLang === "es"
        ? "Reemplazo de burletes y sellado en Miami. Silicona y EPDM de calidad marina para clima costero de Florida. Detenga corrientes de aire, filtraciones y p\u00E9rdida de energ\u00EDa."
        : "Miami weatherstripping replacement and sealing. Marine-grade silicone and EPDM for Florida coastal climate. Stop drafts, water intrusion, and energy loss."
    );

    const cleanupSeo = injectMetaTags({
      title: currentLang === "es" ? "Burletes y Sellado Miami | Sellos de Calidad Marina" : "Weatherstripping & Sealing Miami | Stop Drafts and Water Intrusion",
      description: currentLang === "es" ? "Reemplazo de burletes y sellado en Miami. Sellos de silicona marina y EPDM para clima costero de Florida. Detenga corrientes y filtraciones." : "Window and door weatherstripping repair Miami. Marine-grade seals, door sweeps, threshold caulking across Miami-Dade.",
      url: "https://impact-window-repair-miami.com/weatherstripping-sealing/",
      image: "https://impact-window-repair-miami.com/og/og-weatherstrip.jpg",
      imageAlt: "Weatherstripping Miami",
      breadcrumbs: [
        { name: currentLang === "es" ? "Inicio" : "Home", item: "https://impact-window-repair-miami.com/" },
        { name: currentLang === "es" ? "Burletes y Sellado" : "Weatherstripping & Sealing", item: "https://impact-window-repair-miami.com/weatherstripping-sealing/" }
      ],
      type: "website",
    });

    const schema = {
      "@context": "https://schema.org",
      "@type": "Service",
      name: "Weatherstripping and Window Sealing",
      provider: {
        "@type": "LocalBusiness",
        name: "Windows and Doors Repair LLC",
        telephone: "+17863774452",
        email: "contact@impact-window-repair-miami.com",
        url: "https://impact-window-repair-miami.com",
        address: {
          "@type": "PostalAddress",
          streetAddress: "848 Brickell Ave, PH-5",
          addressLocality: "Miami",
          addressRegion: "FL",
          postalCode: "33131",
          addressCountry: "US",
        },
        geo: {
          "@type": "GeoCoordinates",
          "latitude": 25.7617,
          "longitude": -80.1918
        },
        openingHoursSpecification: [{
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
          "opens": "07:00",
          "closes": "21:00"
        }],
        priceRange: "$$",
        paymentAccepted: ["Cash", "Credit Card", "Debit Card"],
        currenciesAccepted: "USD",
        areaServed: [
          {"@type": "City", "name": "Miami"},
          {"@type": "AdministrativeArea", "name": "Miami-Dade County"},
          "Brickell", "Aventura", "Miami Beach", "Coral Gables", "Coconut Grove", "Doral", "Kendall", "Pinecrest", "Bay Harbor Islands", "Key Biscayne", "South Miami", "North Miami Beach"
        ],
        knowsLanguage: ["en", "es"],
        knowsAbout: ["Window Hardware Repair", "Sliding Door Repair", "Window Glass Replacement", "Weatherstripping", "Window Maintenance", "Emergency Board-Up"],
        slogan: "Honest Miami Window Repair. Same-Day. 30-Day Guarantee.",
        inLanguage: currentLang === "es" ? "es-US" : "en-US"
      },
      serviceType: "Window and Door Sealing and Weatherstripping Repair",
      description:
        "Weatherstripping replacement, caulking, sealing, and leak diagnosis for windows and doors across Miami-Dade County.",
    };

    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: weatherFaqs.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.answer,
        },
      })),
    };

    const script = document.createElement("script");
    script.setAttribute("type", "application/ld+json");
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    const faqScript = document.createElement("script");
    faqScript.setAttribute("type", "application/ld+json");
    faqScript.id = "faq-schema";
    faqScript.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(faqScript);

    return () => {
      cleanupSeo();
      document.head.removeChild(script);
      document.head.removeChild(faqScript);
    };
  }, [currentLang]);

  return (
    <div className="font-roboto">
      <HeaderStrip />
      <HeroSection />
      <LeadFormSection defaultService="weather" />
      <AboutServiceSection
        badge={t("weather.eyebrow") as string}
        imageSrc={weatherAboutImg}
        imageAlt="Miami specialist installing weatherstripping on patio sliding door threshold"
        heading={t("weather.aboutHeading") as string}
        lead={t("weather.intro") as string}
        bullets={[
          t("weather.aboutBullet1") as string,
          t("weather.aboutBullet2") as string,
          t("weather.aboutBullet3") as string,
          t("weather.aboutBullet4") as string,
          t("weather.aboutBullet5") as string,
        ]}
        closing={t("weather.aboutClosing") as string}
      />
      <StatsSection />
      <WhatWeDo />
      <WhyItMatters />
      <CommonIssues />
      <ScopeSection />
      <NeighborhoodCoverage />
      <FaqSection items={weatherFaqs} heading={t("weather.faqH2") as string} />
      <ServiceAreasGrid serviceName="Weatherstripping & Sealing" serviceSlug="weatherstripping-sealing" />
      <RelatedServicesBlockService
        links={[
          { label: t("weather.related1") as string, path: "/sliding-door-repair/" },
          { label: t("weather.related2") as string, path: "/window-hardware-repair/" },
          { label: t("weather.related3") as string, path: "/emergency-board-up/" },
        ]}
      />
      <BottomCta />
    </div>
  );
}